/**
 * Website Template Base Component
 *
 * Provides shared infrastructure for all dealership website templates:
 * - SEO metadata
 * - Vehicle inventory integration
 * - Mobile responsiveness
 * - Structured data for search engines
 */

import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

export interface WebsiteBranding {
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
}

export interface WebsiteHero {
  headline: string;
  subheadline: string;
  ctaLabel: string;
}

export interface WebsiteContact {
  phone: string;
  email: string;
  address: string;
  whatsapp?: string;
  text?: string;
}

export interface WebsiteDomain {
  subdomain: string;
  customDomain: string;
}

export interface WebsiteFeatures {
  inventorySync: boolean;
  financingCta: boolean;
  tradeInCta: boolean;
  leadCapture: boolean;
}

export interface WebsiteAnalytics {
  gaMeasurementId: string;
}

export interface WebsiteSettings {
  templateId: string;
  branding: WebsiteBranding;
  hero: WebsiteHero;
  contact: WebsiteContact;
  domain: WebsiteDomain;
  features: WebsiteFeatures;
  analytics?: WebsiteAnalytics;
  dealershipName?: string;
  bhphBadgeImageUrl?: string;
}

export interface Vehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  vin: string;
  price?: number;
  mileage?: number;
  exterior_color?: string;
  interior_color?: string;
  fuel_type?: string;
  transmission?: string;
  body_style?: string;
  image_url?: string;
  images?: string[];
  status: string;
  description?: string;
  asking_price?: number;
  bhph_eligible?: boolean;
  bhph_min_down_type?: string;
  bhph_min_down_value?: number;
  bhph_payment_amount?: number;
  bhph_payment_frequency?: string;
  bhph_term_months?: number;
}

export interface WebsiteTemplateProps {
  settings: WebsiteSettings;
  vehicles: Vehicle[];
  isPreview?: boolean;
}

export const WebsiteSEO: React.FC<{
  settings: WebsiteSettings;
  vehicles: Vehicle[];
}> = ({ settings, vehicles }) => {
  const dealershipName =
    settings.dealershipName || settings.domain.customDomain || 'Our Dealership';
  const siteUrl =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : settings.domain.customDomain
        ? `https://${settings.domain.customDomain}`
        : '';

  const description = `${dealershipName} - ${settings.hero.subheadline}. Browse our inventory of ${vehicles.length} quality used vehicles.`;

  // Generate structured data for search engines
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    name: dealershipName,
    description: settings.hero.subheadline,
    url: siteUrl,
    telephone: settings.contact.phone,
    email: settings.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: settings.contact.address,
    },
    offers: vehicles.slice(0, 20).map((vehicle) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Car',
        name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
        vehicleIdentificationNumber: vehicle.vin,
        bodyType: vehicle.body_style,
        mileageFromOdometer: {
          '@type': 'QuantitativeValue',
          value: vehicle.mileage,
          unitCode: 'SMI',
        },
        vehicleTransmission: vehicle.transmission,
        fuelType: vehicle.fuel_type,
        color: vehicle.exterior_color,
      },
      price: vehicle.price,
      priceCurrency: 'USD',
    })),
  };

  return (
    <HelmetProvider>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{dealershipName} - Quality Used Vehicles</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content={`used cars, ${dealershipName}, buy here pay here, auto financing, quality vehicles`}
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={`${dealershipName} - Quality Used Vehicles`} />
        <meta property="og:description" content={description} />
        {vehicles[0]?.image_url && <meta property="og:image" content={vehicles[0].image_url} />}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={siteUrl} />
        <meta property="twitter:title" content={`${dealershipName} - Quality Used Vehicles`} />
        <meta property="twitter:description" content={description} />
        {vehicles[0]?.image_url && (
          <meta property="twitter:image" content={vehicles[0].image_url} />
        )}

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content={settings.branding.primaryColor} />
      </Helmet>
    </HelmetProvider>
  );
};

export const formatPrice = (price?: number | null): string => {
  if (!price || isNaN(price)) return 'Contact for Price';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
};

export const formatMileage = (mileage?: number | null): string => {
  if (!mileage || isNaN(mileage)) return 'N/A';
  return new Intl.NumberFormat('en-US').format(mileage) + ' mi';
};

// Make-based Unsplash fallback images (used when vehicle has no photos)
const MAKE_IMAGES: Record<string, string> = {
  BMW: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=600&h=380&fit=crop&auto=format',
  MERCEDES:
    'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=600&h=380&fit=crop&auto=format',
  TOYOTA:
    'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&h=380&fit=crop&auto=format',
  HONDA:
    'https://images.unsplash.com/photo-1590362891991-f776e747a588?w=600&h=380&fit=crop&auto=format',
  FORD: 'https://images.unsplash.com/photo-1551830820-330a71b99659?w=600&h=380&fit=crop&auto=format',
  CHEVROLET:
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&h=380&fit=crop&auto=format',
  VOLKSWAGEN:
    'https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?w=600&h=380&fit=crop&auto=format',
  AUDI: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=600&h=380&fit=crop&auto=format',
  LEXUS:
    'https://images.unsplash.com/photo-1563720223185-11003d516935?w=600&h=380&fit=crop&auto=format',
  NISSAN:
    'https://images.unsplash.com/photo-1580274455191-1c62238fa333?w=600&h=380&fit=crop&auto=format',
  HYUNDAI:
    'https://images.unsplash.com/photo-1629897048514-3dd7414fe72a?w=600&h=380&fit=crop&auto=format',
  KIA: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=600&h=380&fit=crop&auto=format',
  DEFAULT:
    'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&h=380&fit=crop&auto=format',
};

const ensureHttps = (url: string): string =>
  url.startsWith('http://') ? url.replace('http://', 'https://') : url;

export const getVehicleImage = (vehicle: Vehicle): string => {
  if (vehicle.image_url) return ensureHttps(vehicle.image_url);
  if (vehicle.images && vehicle.images.length > 0) {
    return ensureHttps(vehicle.images[0]);
  }
  // Fallback: make-based Unsplash photo
  const makeKey = (vehicle.make || '').toUpperCase().split(' ')[0];
  return MAKE_IMAGES[makeKey] || MAKE_IMAGES.DEFAULT;
};

export const VehicleCard: React.FC<{
  vehicle: Vehicle;
  branding: WebsiteBranding;
  onContact?: () => void;
}> = ({ vehicle, branding, onContact }) => {
  const imageUrl = getVehicleImage(vehicle);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
      <div className="aspect-[4/3] overflow-hidden bg-gray-200">
        <img
          src={imageUrl}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/vehicle_placeholder.svg';
          }}
        />
      </div>
      <div className="p-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        {vehicle.trim && <p className="text-sm text-gray-600 mb-2">{vehicle.trim}</p>}
        <div className="flex items-center justify-between mb-3">
          <span className="text-2xl font-bold" style={{ color: branding.primaryColor }}>
            {formatPrice(vehicle.price)}
          </span>
          <span className="text-sm text-gray-600">{formatMileage(vehicle.mileage)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-4">
          {vehicle.transmission && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Trans:</span> {vehicle.transmission}
            </div>
          )}
          {vehicle.fuel_type && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Fuel:</span> {vehicle.fuel_type}
            </div>
          )}
          {vehicle.exterior_color && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Exterior:</span> {vehicle.exterior_color}
            </div>
          )}
          {vehicle.body_style && (
            <div className="flex items-center gap-1">
              <span className="font-medium">Body:</span> {vehicle.body_style}
            </div>
          )}
        </div>
        <button
          onClick={onContact}
          className="w-full py-2 px-4 rounded-md text-white font-semibold hover:opacity-90 transition-opacity"
          style={{ backgroundColor: branding.accentColor }}
        >
          Contact Us
        </button>
      </div>
    </div>
  );
};

export const ContactSection: React.FC<{
  contact: WebsiteContact;
  branding: WebsiteBranding;
  dealershipName: string;
}> = ({ contact, branding, dealershipName: _dealershipName }) => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p className="text-lg text-gray-600">Visit us today or get in touch with our team</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div
              className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
            <a
              href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`}
              className="text-gray-600 hover:text-gray-900"
            >
              {contact.phone}
            </a>
            {contact.text && (
              <a
                href={`sms:${contact.text.replace(/[^0-9+]/g, '')}`}
                className="text-gray-600 hover:text-gray-900 block mt-1"
              >
                Text: {contact.text}
              </a>
            )}
            {contact.whatsapp && (
              <a
                href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 block mt-1"
              >
                WhatsApp: {contact.whatsapp}
              </a>
            )}
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div
              className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
            <a href={`mailto:${contact.email}`} className="text-gray-600 hover:text-gray-900">
              {contact.email}
            </a>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div
              className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Location</h3>
            <p className="text-gray-600">{contact.address}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
